/**
 * Reservation Management Module - Mutation Services
 *
 * POST hooks for TaskManagement endpoints.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  EARLY_CHECKIN_VALIDATE_URL,
  EARLY_CHECKIN_APPLY_URL,
  LATE_CHECKOUT_VALIDATE_URL,
  LATE_CHECKOUT_APPLY_URL,
  EXTEND_STAY_VALIDATE_URL,
  EXTEND_STAY_APPLY_URL,
  CONFIRM_CHECKOUT_URL,
  MOVE_TO_PROPERTY_URL,
  TASK_MANAGEMENT_URL,
  TASK_MANAGEMENT_STATS_URL,
  RESERVATION_LIST_URL,
  RESERVATION_DETAIL_URL,
} from '../constants';
import type {
  IApiResponse,
  IEarlyCheckinRequest,
  IEarlyCheckinValidateResult,
  ILateCheckoutRequest,
  ILateCheckoutValidateResult,
  IExtendStayRequest,
  IExtendStayValidateResult,
  IMoveToPropertyRequest,
} from '../types';

/**
 * Validate an early check-in request.
 * POST /api/TaskManagement/EarlyCheckin/Validate
 * Returns 200 when valid, 422 when isValid = false.
 */
export const useEarlyCheckinValidate = () => {
  const { apiPrivate } = useApi();

  return useMutation<IApiResponse<IEarlyCheckinValidateResult>, Error, IEarlyCheckinRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<IEarlyCheckinValidateResult>>(
        EARLY_CHECKIN_VALIDATE_URL,
        payload
      );
      return data;
    },
  });
};

/**
 * Apply an early check-in.
 * POST /api/TaskManagement/EarlyCheckin/Apply
 * Updates CheckInDate and saves a ReservationChangeRequest audit record.
 * Returns 422 if validation fails (nothing is saved).
 */
export const useEarlyCheckinApply = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<IApiResponse<unknown>, Error, IEarlyCheckinRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<unknown>>(
        EARLY_CHECKIN_APPLY_URL,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    },
  });
};

/**
 * Validate a late checkout request.
 * POST /api/TaskManagement/LateCheckout/Validate
 */
export const useLateCheckoutValidate = () => {
  const { apiPrivate } = useApi();

  return useMutation<IApiResponse<ILateCheckoutValidateResult>, Error, ILateCheckoutRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<ILateCheckoutValidateResult>>(
        LATE_CHECKOUT_VALIDATE_URL,
        payload
      );
      return data;
    },
  });
};

/**
 * Apply a late checkout.
 * POST /api/TaskManagement/LateCheckout/Apply
 */
export const useLateCheckoutApply = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<IApiResponse<unknown>, Error, ILateCheckoutRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<unknown>>(
        LATE_CHECKOUT_APPLY_URL,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    },
  });
};

/**
 * Validate a stay extension request.
 * POST /api/TaskManagement/ExtendStay/Validate
 */
export const useExtendStayValidate = () => {
  const { apiPrivate } = useApi();

  return useMutation<IApiResponse<IExtendStayValidateResult>, Error, IExtendStayRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<IExtendStayValidateResult>>(
        EXTEND_STAY_VALIDATE_URL,
        payload
      );
      return data;
    },
  });
};

/**
 * Apply a stay extension.
 * POST /api/TaskManagement/ExtendStay/Apply
 */
export const useExtendStayApply = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<IApiResponse<unknown>, Error, IExtendStayRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<unknown>>(
        EXTEND_STAY_APPLY_URL,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    },
  });
};

/**
 * Confirm a guest checkout.
 * POST /api/TaskManagement/ConfirmCheckout
 */
export const useConfirmCheckout = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<IApiResponse<unknown>, Error, { reservationId: number }>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<unknown>>(
        CONFIRM_CHECKOUT_URL,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    },
  });
};

/**
 * Move a reservation extension to another property.
 * POST /api/TaskManagement/Extension/MoveToProperty
 */
export const useMoveToProperty = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<IApiResponse<unknown>, Error, IMoveToPropertyRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiPrivate.post<IApiResponse<unknown>>(
        MOVE_TO_PROPERTY_URL,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_URL] });
      queryClient.invalidateQueries({ queryKey: [TASK_MANAGEMENT_STATS_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_LIST_URL] });
      queryClient.invalidateQueries({ queryKey: [RESERVATION_DETAIL_URL] });
    },
  });
};
